<script lang="ts" setup>
import type { Charge } from '@/services/order'

import Button from '@/components/Button.vue'
import Badge from '@/components/Badge.vue'
import ProductImageBubbleList from '@/components/ProductImageBubbleList.vue'
import { formatDate } from '@/lib/formatters/date'
import type { ImageMap } from '@/services/productImage'

import IconPencil from '@/assets/icons/pencil.svg?component'

const props = defineProps<{
  order: Charge
  imagesByProductId: Record<string, ImageMap[] | null | undefined>
}>()

const emit = defineEmits<{
  (e: 'edit'): void
}>()

const getImagesForProduct = (id: string) => {
  const imageList = props.imagesByProductId[id]
  if (imageList) {
    return imageList[0]
  } else {
    return undefined
  }
}
</script>

<template>
  <div class="NextOrderPreview">
    <span class="NextOrderPreview__OrderMetaTitle">Your next order</span>
    <div class="NextOrderPreview__OrderMetaDate">
      <Badge class="Badge--success-alt"
        >Processes {{ formatDate('DD Mon', order.scheduled_at) }}</Badge
      >
    </div>
    <div class="NextOrderPreview__OrderMeta">
      <Badge class="NextOrderPreview__OrderMeta__Date Badge--success-alt"
        >Processes {{ formatDate('DD Mon', order.scheduled_at) }}</Badge
      >
      <Badge class="Badge--mid">
        {{ order.line_items.reduce((acc, item) => acc + item.quantity, 0) }}
        items
      </Badge>
      <Badge class="Badge--mid">£{{ order.total_price }}</Badge>
    </div>
    <div class="NextOrderPreview__ProductImages">
      <ProductImageBubbleList
        :max-items="2"
        :images="
          order.line_items.map((li) => ({
            url: getImagesForProduct(
              `${li.external_product_id.ecommerce}:${li.external_variant_id.ecommerce}`,
            )?.small,
            alt: li.title,
          }))
        "
      ></ProductImageBubbleList>
    </div>
    <div class="NextOrderPreview__Actions">
      <Button :block="true" @click="emit('edit')">
        <template #iconBefore><IconPencil /></template>
        Add, edit or reschedule
      </Button>
    </div>
  </div>
</template>

<style>
.NextOrderPreview {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  grid-template-rows: auto auto auto auto;
  background-color: var(--color-white);
  border-radius: 8px;
  font-size: 14px;
  padding: 12px 16px;
  position: relative;
  gap: 8px 0;

  @media (--mq-tablet-and-up) {
    padding: 20px;
  }
}

.NextOrderPreview__OrderMetaTitle {
  font-size: 16px;
  font-weight: 700;
  grid-column: 1 / 5;
  grid-row: 1;

  @media (--mq-tablet-and-up) {
    grid-column: 1 / 5;
  }
}

.NextOrderPreview__OrderMetaDate {
  grid-column: 5 / 9;
  grid-row: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media (--mq-tablet-and-up) {
    display: none;
  }
}

.NextOrderPreview__OrderMeta {
  grid-column: 7 / 9;
  grid-row: 2 / 4;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  gap: 8px;

  @media (--mq-tablet-and-up) {
    grid-column: 1 / 5;
    grid-row: 2;
    align-items: flex-start;
    flex-direction: row;
  }
}

.Badge.NextOrderPreview__OrderMeta__Date {
  display: none;

  @media (--mq-tablet-and-up) {
    display: inline-flex;
  }
}

.NextOrderPreview__OrderMeta span {
  color: var(--color-forest-50);
  padding: 0 5px;

  @media (--mq-tablet-and-up) {
    color: var(--color-forest-100);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
  }
}

.NextOrderPreview__ProductImages {
  grid-column: 1 / 7;
  grid-row: 2 / 4;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  .ProductImageBubbleList {
    justify-content: flex-start;
  }

  @media (--mq-tablet-and-up) {
    grid-column: 5 / 9;
    grid-row: 1 / 4;
    justify-content: flex-end;

    .ProductImageBubbleList {
      justify-content: flex-end;
      padding-right: 20px;
    }
  }
}

.NextOrderPreview__Actions {
  grid-column: 1 / 9;
  grid-row: 4;
  padding-top: 8px;

  @media (--mq-tablet-and-up) {
    grid-column: 1 / 5;
    grid-row: 3;

    & .Button {
      width: auto;
    }
  }
}
</style>
